import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';
import type { DashboardProcessosApi, DashboardProcessosQuery } from '../types/dashboardTypes';
import type {
  ArbitralExportStatusResponse,
  ExportProcessosArbitraisParams,
  FetchProcessosArbitraisParams,
  ListProcessosArbitraisResponse,
  SituacaoOption,
  StartArbitralExportResponse,
} from '../types/processosArbitraisStoreTypes';
import { getMockProcessosArbitrais } from '../utils/loteArbitralStorage';
import { processosArbitraisService } from './services';

const shouldMergeMockLote = (params?: FetchProcessosArbitraisParams): boolean => {
  if (!params) return true;
  const semBusca = !params.search;
  const semFiltros = (params.status?.length ?? 0) === 0 && (params.rito?.length ?? 0) === 0;
  const primeiraPagina = (params.page ?? 1) === 1;
  const abaCompativel = params.tab !== 'CONCLUDED';
  return semBusca && semFiltros && primeiraPagina && abaCompativel;
};

const mergeMockLote = (
  response: ListProcessosArbitraisResponse,
  params?: FetchProcessosArbitraisParams,
): ListProcessosArbitraisResponse => {
  const mockItems = getMockProcessosArbitrais();
  if (mockItems.length === 0 || !shouldMergeMockLote(params)) return response;

  return {
    ...response,
    items: [...mockItems, ...response.items],
    summary: {
      ...response.summary,
      total: response.summary.total + mockItems.length,
    },
    pagination: {
      ...response.pagination,
      total: response.pagination.total + mockItems.length,
    },
  };
};

const buildMockOnlyResponse = (
  params?: FetchProcessosArbitraisParams,
): ListProcessosArbitraisResponse | null => {
  const mockItems = getMockProcessosArbitrais();
  if (mockItems.length === 0 || !shouldMergeMockLote(params)) return null;

  return {
    items: mockItems,
    pagination: { page: 1, limit: params?.limit ?? 20, total: mockItems.length, totalPages: 1 },
    summary: {
      total: mockItems.length,
      pendingAction: 0,
      awaitingPayment: 0,
      concluded: 0,
    },
  };
};

export const fetchProcessosArbitrais = createAsyncThunk<
  ListProcessosArbitraisResponse,
  FetchProcessosArbitraisParams | undefined,
  { rejectValue: string; state: RootState }
>('arbitral/fetchProcessosArbitrais', async (params, { rejectWithValue }) => {
  try {
    const response = await processosArbitraisService.list(params);
    return mergeMockLote(response, params);
  } catch (error) {
    const mockOnly = buildMockOnlyResponse(params);
    if (mockOnly) return mockOnly;
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchProcessosArbitraisSituacaoOptions = createAsyncThunk<
  SituacaoOption[],
  void,
  { rejectValue: string; state: RootState }
>('arbitral/fetchSituacaoOptions', async (_, { rejectWithValue }) => {
  try {
    return await processosArbitraisService.listSituacaoOptions();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const reprocessarInicioProcessoArbitral = createAsyncThunk<
  void,
  string,
  { rejectValue: string; state: RootState }
>('arbitral/reprocessarInicioProcessoArbitral', async (processoId, { rejectWithValue }) => {
  try {
    await processosArbitraisService.reprocessarInicio(processoId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const startProcessosArbitraisExport = createAsyncThunk<
  StartArbitralExportResponse,
  ExportProcessosArbitraisParams,
  { rejectValue: string; state: RootState }
>('arbitral/startProcessosArbitraisExport', async (params, { rejectWithValue }) => {
  try {
    return await processosArbitraisService.startExport(params);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchDashboardProcessosArbitrais = createAsyncThunk<
  DashboardProcessosApi,
  DashboardProcessosQuery,
  { rejectValue: string; state: RootState }
>('arbitral/fetchDashboardProcessos', async (query, { rejectWithValue, signal }) => {
  try {
    // O `signal` cancela a requisição de rede quando o recorte muda (`pedido.abort()` do hook).
    return await processosArbitraisService.dashboard(query, signal);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const pollProcessosArbitraisExport = createAsyncThunk<
  ArbitralExportStatusResponse,
  string,
  { rejectValue: string; state: RootState }
>('arbitral/pollProcessosArbitraisExport', async (exportId, { rejectWithValue }) => {
  try {
    return await processosArbitraisService.getExportStatus(exportId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});
